<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import router from '../../router/routes';
import PreviewHeader from '@/components/PreviewHeader.vue';
import Reminder from '@/components/Reminder.vue';
import InlineSpinner from '@/components/InlineSpinner.vue';

import { useUserStore } from '@/stores/user';
import { useLeadMatchingStore } from '@/stores/leadMatching';
import { useBusinessPartnersCountStore } from '@/stores/businessPartnersCount';

const userStore = useUserStore();
const leadMatchingStore = useLeadMatchingStore();
const businessPartnersCountStore = useBusinessPartnersCountStore();

const DEFAULT_FORM = {
  name: '',
  street: '',
  houseNumber: '',
  postalCode: '',
  city: '',
  url: '',
  phoneNumber: '',
};

const MAX_EIGENRECHERCHE = computed(() => userStore.maxReservations ?? 100);
const eigenrechercheCount = ref(0);

const cooldown = ref(3);
const isCooldownActive = ref(true);
let cooldownInterval = null;

function ensureFormState() {
  const current = leadMatchingStore.lastFormLead;
  if (!current || typeof current !== 'object') {
    leadMatchingStore.setLastFormLead({ ...DEFAULT_FORM });
    return;
  }
  leadMatchingStore.setLastFormLead({ ...DEFAULT_FORM, ...current });
}

function startCooldown() {
  cooldown.value = 3;
  isCooldownActive.value = true;
  if (cooldownInterval) {
    clearInterval(cooldownInterval);
    cooldownInterval = null;
  }
  cooldownInterval = setInterval(() => {
    if (cooldown.value > 0) {
      cooldown.value -= 1;
    } else {
      isCooldownActive.value = false;
      clearInterval(cooldownInterval);
      cooldownInterval = null;
    }
  }, 1000);
}

async function loadCapacity() {
  const filter = businessPartnersCountStore.namedFilter('eigenrechercheUnbeendetCount');
  const targetSignature = filter?.signature;

  let lists = businessPartnersCountStore.businessPartnerLists.find((l) =>
    (l.signature && l.signature === targetSignature)
    || (l.filter && l.filter.signature && l.filter.signature === targetSignature)
  );

  if (!lists) {
    await businessPartnersCountStore.getByFilter(filter);
    lists = businessPartnersCountStore.businessPartnerLists.find((l) =>
      (l.signature && l.signature === targetSignature)
      || (l.filter && l.filter.signature && l.filter.signature === targetSignature)
    );
  }

  if (lists && typeof lists.count === 'number') {
    eigenrechercheCount.value = lists.count;
  } else {
    eigenrechercheCount.value = 0;
  }
}

const formLead = computed(() => leadMatchingStore.lastFormLead || DEFAULT_FORM);

const fieldErrors = computed(() => {
  const missing = [];
  if (!String(formLead.value.name || '').trim()) missing.push('Firmenname fehlt');
  if (!String(formLead.value.street || '').trim()) missing.push('Straße fehlt');
  if (!String(formLead.value.houseNumber || '').trim()) missing.push('Hausnummer fehlt');
  if (!String(formLead.value.postalCode || '').trim()) missing.push('Postleitzahl fehlt');
  if (!String(formLead.value.city || '').trim()) missing.push('Ort fehlt');
  return missing;
});

const isAtCapacity = computed(() => eigenrechercheCount.value >= MAX_EIGENRECHERCHE.value);
const isOverCapacity = computed(() => eigenrechercheCount.value > MAX_EIGENRECHERCHE.value);

const canConvert = computed(() => {
  return (
    !isCooldownActive.value
    && !userStore.currentSelection.leadMatching.isLoading
    && !isOverCapacity.value
    && fieldErrors.value.length === 0
  );
});

const candidates = computed(() => {
  const list = leadMatchingStore.businessPartnerList?.data || [];
  return [...list].sort((a, b) => candidateScoreValue(b) - candidateScoreValue(a));
});

const activeSignatureLabel = computed(() => {
  const sig = userStore.currentSelection?.leadMatching?.signature || '';
  if (!sig) return 'SMA Formular';
  return decodeURIComponent(String(sig).replace(/^googlePlacesSearch:/, '') || 'SMA Formular');
});

function candidateAddress(lead) {
  return lead?.meta?.normalization?.address?.split(',').slice(0, 2).join(', ') || '—';
}

function candidatePhones(lead) {
  return lead?.meta?.normalization?.phoneNumbers?.join(' | ') || '—';
}

function candidateUrls(lead) {
  return lead?.meta?.normalization?.urls?.join(' | ') || '—';
}

function candidateScore(lead) {
  const score = candidateScoreValue(lead);
  if (score === null) return '--';
  return Math.round(score);
}

function candidateScoreValue(lead) {
  const confidence = Number(lead?.confidence);
  if (!Number.isFinite(confidence)) return null;
  return confidence <= 1 ? confidence * 100 : confidence;
}

function candidateGpNo(lead) {
  return lead?.meta?.gpNo || '—';
}

function businessPartnerLink(lead) {
  const gpNo = lead?.meta?.gpNo;
  if (!gpNo) return null;
  return {
    name: 'BusinessPartner',
    query: {
      searchMode: 'GPNo',
      search: gpNo,
      fromResearchList: 'true',
    },
  };
}

async function handleConvertLeads() {
  if (!canConvert.value) return;
  userStore.currentSelection.leadMatching.isLoading = true;
  try {
    const result = await leadMatchingStore.convertLeads();
    if (result) {
      leadMatchingStore.removeQuery(userStore.currentSelection.leadMatching.signature);
      await userStore.refreshFilterOptions();
      router.push({ name: 'Dashboard', hash: '#lead-matching' });
    }
  } finally {
    userStore.currentSelection.leadMatching.isLoading = false;
  }
}

function handleAbortConversion() {
  leadMatchingStore.removeQuery(userStore.currentSelection.leadMatching.signature);
  router.push({ name: 'Dashboard', hash: '#lead-matching' });
}

onMounted(async () => {
  ensureFormState();
  startCooldown();
  await loadCapacity();
});

onUnmounted(() => {
  if (cooldownInterval) {
    clearInterval(cooldownInterval);
    cooldownInterval = null;
  }
  userStore.currentSelection.leadMatching.isLoading = false;
});
</script>

<template>
  <div class="reminder-wrapper singlepage-wrapper lead-matching-page max-width">
    <main id="container-main">
      <PreviewHeader
        :title="'Lead Matching'"
        :to="'/dashboard#lead-matching'"
        button-label="Zur Übersicht"
        arrow="left"
      />

      <div v-if="userStore.currentSelection.leadMatching.search.isLoading" class="container main-content cell-grid">
        <InlineSpinner size="lg" layout="column" label="Matching-Daten werden geladen..." />
      </div>

      <div class="lead-matching-content-row">
        <div class="matching-summary-card">
          <div class="matching-summary-head">
            <div>
              <p class="matching-eyebrow">SMA Formular</p>
              <h2>Geschäftspartner neu anlegen</h2>
              <p class="matching-subline">Bestehende Treffer prüfen und anschließend den neuen Datensatz gezielt anlegen.</p>
            </div>
            <div class="matching-pills">
              <span class="matching-pill">{{ activeSignatureLabel }}</span>
              <span class="matching-pill">Kandidaten: <b>{{ candidates.length }}</b></span>
              <span class="matching-pill matching-pill--capacity">Kapazität: <b>{{ eigenrechercheCount }}/{{ MAX_EIGENRECHERCHE }}</b></span>
            </div>
          </div>

          <div class="matching-form-wrap">
            <form class="matching-form" @submit.prevent="handleConvertLeads" novalidate>
              <div class="form-grid-lead-matching">
                <div class="login-field full-width">
                  <label for="lm-name" class="sr-only">Firmenname*</label>
                  <input id="lm-name" class="login-input" type="text" v-model.trim="leadMatchingStore.lastFormLead.name" placeholder="Firmenname*" required>
                </div>
                <div class="login-field street">
                  <label for="lm-street" class="sr-only">Straße*</label>
                  <input id="lm-street" class="login-input" type="text" v-model.trim="leadMatchingStore.lastFormLead.street" placeholder="Straße*" required>
                </div>
                <div class="login-field house-number">
                  <label for="lm-house" class="sr-only">Hausnummer*</label>
                  <input id="lm-house" class="login-input" type="text" v-model.trim="leadMatchingStore.lastFormLead.houseNumber" placeholder="Hausnummer*" required>
                </div>
                <div class="login-field postal-code">
                  <label for="lm-postal" class="sr-only">Postleitzahl*</label>
                  <input id="lm-postal" class="login-input" type="text" v-model.trim="leadMatchingStore.lastFormLead.postalCode" placeholder="Postleitzahl*" inputmode="numeric" required>
                </div>
                <div class="login-field city">
                  <label for="lm-city" class="sr-only">Ort*</label>
                  <input id="lm-city" class="login-input" type="text" v-model.trim="leadMatchingStore.lastFormLead.city" placeholder="Ort*" required>
                </div>
                <div class="login-field full-width">
                  <label for="lm-url" class="sr-only">Website</label>
                  <input id="lm-url" class="login-input" type="url" v-model.trim="leadMatchingStore.lastFormLead.url" placeholder="Website (optional)">
                </div>
                <div class="login-field full-width">
                  <label for="lm-phone" class="sr-only">Telefonnummer</label>
                  <input id="lm-phone" class="login-input" type="tel" v-model.trim="leadMatchingStore.lastFormLead.phoneNumber" placeholder="Telefon (optional)">
                </div>
              </div>

              <div class="matching-actions">
                <button type="submit" class="btn btn--solid" :disabled="!canConvert">
                  Datensatz anlegen
                  <span v-if="isCooldownActive">({{ cooldown }}s)</span>
                </button>
                <button type="button" class="btn btn--ghost" @click="handleAbortConversion">Abbrechen</button>
              </div>

              <div v-if="fieldErrors.length || isOverCapacity" class="form-errors" role="alert">
                <div v-if="isOverCapacity">Kapazitätslimit erreicht: Bitte bestehende Eigenrecherche-Neukunden zuerst bearbeiten.</div>
                <ul v-if="fieldErrors.length">
                  <li v-for="err in fieldErrors" :key="err">{{ err }}</li>
                </ul>
              </div>
            </form>
          </div>
        </div>

        <div class="widget-wrapper lead-matching">
          <div class="container-wrapper">
            <div class="container" ref="businessPartnerListDesktop">
              <h2 class="container-headline">Bestehende Geschäfts­partner</h2>
              <p class="container-subline">Diese Treffer wurden im Bestand gefunden und dienen als Duplikat-Prüfung.</p>

              <div v-if="!candidates.length" class="empty-state">
                Keine Matching-Treffer vorhanden.
              </div>

              <div v-else class="table-responsive">
                <table class="sticky-table">
                  <thead>
                    <tr>
                      <th class="col-gp">GP</th>
                      <th class="col-name">Name / Firma</th>
                      <th class="col-address">Adresse</th>
                      <th class="col-phone">Telefon</th>
                      <th class="col-url">URL</th>
                      <th class="col-score">Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="lead in candidates" :key="lead.id || `${lead.name}-${lead.meta?.gpNo}`">
                      <td class="col-gp">
                        <router-link v-if="businessPartnerLink(lead)" :to="businessPartnerLink(lead)">{{ candidateGpNo(lead) }}</router-link>
                        <span v-else>{{ candidateGpNo(lead) }}</span>
                      </td>
                      <td class="col-name">{{ lead.name || '—' }}</td>
                      <td class="col-address"><span class="address-text">{{ candidateAddress(lead) }}</span></td>
                      <td class="col-phone">{{ candidatePhones(lead) }}</td>
                      <td class="col-url">{{ candidateUrls(lead) }}</td>
                      <td class="col-score">
                        <span class="score-pill" :class="{ low: candidateScore(lead) !== '--' && candidateScore(lead) < 40 }">{{ candidateScore(lead) }}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
    <Reminder />

    <div v-if="userStore.currentSelection.leadMatching.isLoading" class="fullscreen-spinner-overlay">
      <div class="spinner-wrapper">
        <InlineSpinner size="lg" layout="column" label="Datensatz wird angelegt..." />
      </div>
    </div>
  </div>
</template>

<style scoped>
.lead-matching-page {
  position: relative;
}

.lead-matching-page #container-main {
  display: flex;
  flex-direction: column;
  gap: .85rem;
}

.lead-matching-content-row {
  display: grid;
  grid-template-columns: minmax(320px, 460px) minmax(0, 1fr);
  gap: .9rem;
  align-items: start;
}

.lead-matching-content-row .matching-summary-card {
  margin-bottom: 0;
}

.lead-matching-content-row .widget-wrapper.lead-matching {
  min-width: 0;
}

.matching-summary-card {
  background: linear-gradient(145deg, #111b26, #0b1520);
  border: 1px solid rgba(151, 210, 212, 0.25);
  border-radius: 14px;
  padding: .95rem;
  margin-bottom: .9rem;
}

.theme--light .matching-summary-card {
  background: linear-gradient(145deg, #ffffff, #f8fbfd);
  border-color: #d5e4eb;
}

.matching-summary-head h2 {
  margin: 0;
  font-size: 1.05rem;
}

.matching-summary-head {
  text-align: left;
}

.matching-eyebrow {
  margin: 0 0 .2rem;
  font-size: .68rem;
  text-transform: uppercase;
  letter-spacing: .5px;
  opacity: .75;
}

.matching-subline {
  margin: .25rem 0 .65rem;
  font-size: .72rem;
  opacity: .85;
}

.matching-pills {
  display: flex;
  flex-wrap: wrap;
  gap: .4rem;
  justify-content: flex-start;
}

.matching-pill {
  display: inline-flex;
  align-items: center;
  gap: .25rem;
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 999px;
  padding: .2rem .55rem;
  font-size: .67rem;
  background: rgba(15, 23, 42, .35);
}

.theme--light .matching-pill {
  background: #eef4f8;
  border-color: #d2dee6;
}

.matching-pill.matching-pill--capacity {
  color: #e8fff0;
  border-color: rgba(34, 197, 94, .65);
  background: linear-gradient(135deg, rgba(22, 163, 74, .65), rgba(21, 128, 61, .75));
}

.theme--light .matching-pill.matching-pill--capacity {
  color: #14532d;
  border-color: rgba(22, 163, 74, .45);
  background: linear-gradient(135deg, rgba(187, 247, 208, .9), rgba(134, 239, 172, .92));
}

.matching-form-wrap {
  margin-top: .75rem;
}

.form-grid-lead-matching {
  display: grid;
  grid-template-columns: 3fr 1fr;
  gap: .55rem .6rem;
}

.form-grid-lead-matching .full-width {
  grid-column: 1 / 3;
}

.form-grid-lead-matching .street { grid-column: 1 / 2; }
.form-grid-lead-matching .house-number { grid-column: 2 / 3; }
.form-grid-lead-matching .postal-code { grid-column: 1 / 2; }
.form-grid-lead-matching .city { grid-column: 2 / 3; }

.login-field {
  display: flex;
  align-items: center;
  padding: .45rem .55rem;
  border: 1px solid #2c3742;
  border-radius: 10px;
  background: #0f1620;
}

.theme--light .login-field {
  background: #ffffff;
  border-color: #d3dde5;
}

.login-input {
  width: 100%;
  border: 0;
  background: transparent;
  color: #e2e8f0;
  font-size: .83rem;
}

.theme--light .login-input { color: #1e293b; }
.login-input::placeholder { color: #94a3b8; }

.login-input:focus-visible {
  outline: 2px solid var(--sellwerk-color-secondary, #1790da);
  outline-offset: 2px;
  border-radius: 6px;
}

.matching-actions {
  margin-top: .75rem;
  display: flex;
  gap: .5rem;
  justify-content: flex-start;
}

.btn {
  padding: .55rem .9rem;
  border-radius: 10px;
  border: 1px solid transparent;
  font-weight: 700;
  cursor: pointer;
}

.btn--solid {
  background: var(--sellwerk-color-main, #007d72);
  border-color: var(--sellwerk-color-main, #007d72);
  color: #fff;
}

.btn--solid:hover:not(:disabled) {
  background: var(--sellwerk-color-tertiary, #05635c);
  border-color: var(--sellwerk-color-tertiary, #05635c);
}

.btn--ghost {
  background: transparent;
  border-color: #567286;
  color: #cbd5e1;
}

.theme--light .btn--ghost {
  color: #334155;
  border-color: #b4c4cf;
}

.btn:disabled {
  opacity: .62;
  filter: saturate(.55);
  cursor: not-allowed;
}

.form-errors {
  margin-top: .6rem;
  padding: .55rem .65rem;
  border-radius: 10px;
  border: 1px solid #8b1f1f;
  background: #2a1717;
  color: #fecaca;
  font-size: .72rem;
}

.theme--light .form-errors {
  background: #fff1f2;
  border-color: #fda4af;
  color: #7f1d1d;
}

.form-errors ul {
  margin: .3rem 0 0 1rem;
  padding: 0;
}

.widget-wrapper.lead-matching .container {
  padding: .85rem;
}

.container-headline {
  margin: 0;
  font-size: .95rem;
  text-align: center;
}

.container-subline {
  margin: .2rem 0 .65rem;
  font-size: .72rem;
  opacity: .75;
  text-align: center;
}

.empty-state {
  padding: .8rem;
  border: 1px dashed rgba(148, 163, 184, .45);
  border-radius: 10px;
  font-size: .78rem;
  opacity: .85;
  text-align: center;
}

.table-responsive {
  overflow-x: auto;
}

.sticky-table {
  width: 100%;
  border-collapse: collapse;
  font-size: .72rem;
}

.sticky-table th,
.sticky-table td {
  border-bottom: 1px solid rgba(148, 163, 184, .25);
  padding: .46rem .4rem;
  text-align: left;
  vertical-align: top;
}

.sticky-table th {
  font-size: .64rem;
  text-transform: uppercase;
  letter-spacing: .4px;
  opacity: .75;
}

/* Override shared table defaults only for LeadMatching candidate list */
.lead-matching-content-row .widget-wrapper.lead-matching .sticky-table .col-gp {
  width: 96px;
  min-width: 96px;
  max-width: 120px;
}

.lead-matching-content-row .widget-wrapper.lead-matching .sticky-table .col-address {
  width: 34%;
  min-width: 260px;
}

.lead-matching-content-row .widget-wrapper.lead-matching .sticky-table .col-name {
  width: 26%;
  min-width: 220px;
}

.lead-matching-content-row .widget-wrapper.lead-matching .sticky-table .col-phone {
  width: 18%;
  min-width: 180px;
}

.lead-matching-content-row .widget-wrapper.lead-matching .sticky-table .col-url {
  width: 16%;
  min-width: 180px;
}

.lead-matching-content-row .widget-wrapper.lead-matching .sticky-table .col-score {
  width: 90px;
  min-width: 90px;
}

.lead-matching-content-row .widget-wrapper.lead-matching .sticky-table th,
.lead-matching-content-row .widget-wrapper.lead-matching .sticky-table td {
  vertical-align: middle;
}

.score-pill {
  display: inline-flex;
  min-width: 2.4rem;
  justify-content: center;
  padding: .12rem .4rem;
  border-radius: 999px;
  border: 1px solid rgba(34, 197, 94, .45);
  background: rgba(34, 197, 94, .15);
  font-weight: 700;
}

.score-pill.low {
  border-color: rgba(245, 158, 11, .55);
  background: rgba(245, 158, 11, .15);
}

.fullscreen-spinner-overlay {
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, .55);
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
}

.spinner-wrapper {
  background: #fff;
  color: #1f2937;
  border-radius: 12px;
  padding: 1.2rem 1.4rem;
  box-shadow: 0 12px 30px -10px rgba(2, 6, 23, .45);
}

@media (max-width: 920px) {
  .lead-matching-content-row {
    grid-template-columns: 1fr;
    gap: .8rem;
  }

  .lead-matching-content-row .widget-wrapper.lead-matching .sticky-table .col-gp {
    min-width: 84px;
  }

  .lead-matching-content-row .widget-wrapper.lead-matching .sticky-table .col-address {
    min-width: 220px;
  }

  .form-grid-lead-matching {
    grid-template-columns: 1fr;
  }

  .form-grid-lead-matching .full-width,
  .form-grid-lead-matching .street,
  .form-grid-lead-matching .house-number,
  .form-grid-lead-matching .postal-code,
  .form-grid-lead-matching .city {
    grid-column: 1 / 2;
  }

  .matching-actions {
    justify-content: stretch;
    flex-direction: column;
  }

  .matching-actions .btn {
    width: 100%;
  }
}
</style>
